import { lazy, Suspense, type ReactNode } from 'react';
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { MainLayout } from '@/app/layouts/MainLayout';
import {
  MiniCalendarWidgetContainer,
  UpcomingEventsWidgetContainer,
  TodoProgressWidgetContainer,
  FamilyActivityWidgetContainer,
} from '@/app/widgets';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { PublicRoute } from '@/features/auth/components/PublicRoute';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { FamilyGate } from '@/features/family/components/FamilyGate';
import { ErrorBoundary } from '@/shared/components/feedback/ErrorBoundary';
import { LoadingScreen } from '@/shared/components/feedback/LoadingScreen';
import { isDevelopment } from '@/shared/lib/env';

// Lazy load pages
const Dashboard = lazy(
  () => import('@/features/dashboard/pages/DashboardPage')
);
const Demo = lazy(() => import('@/features/demo/pages/DemoPage'));
const MonitoringDashboard = lazy(() =>
  import('@/features/monitoring/pages/MonitoringDashboardPage').then(
    (module) => ({ default: module.MonitoringDashboardPage })
  )
);
const MonitoringConfig = lazy(() =>
  import('@/features/monitoring/pages/MonitoringConfigPage').then((module) => ({
    default: module.MonitoringConfigPage,
  }))
);
const MonitoringLogs = lazy(() =>
  import('@/features/monitoring/pages/MonitoringLogsPage').then((module) => ({
    default: module.MonitoringLogsPage,
  }))
);
const Login = lazy(() => import('@/features/auth/pages/LoginPage'));
const Register = lazy(() => import('@/features/auth/pages/RegisterPage'));
const ForgotPassword = lazy(
  () => import('@/features/auth/pages/ForgotPasswordPage')
);
const ChangePassword = lazy(
  () => import('@/features/auth/pages/ChangePasswordPage')
);
const ResetPassword = lazy(
  () => import('@/features/auth/pages/ResetPasswordPage')
);
const AuthCallback = lazy(
  () => import('@/features/auth/pages/AuthCallbackPage')
);
const AcceptInvite = lazy(
  () => import('@/features/auth/pages/AcceptInvitePage')
);
const NotFound = lazy(
  () => import('@/shared/components/feedback/NotFoundPage')
);
const LovePage = lazy(() => import('@/features/love/pages/LovePage'));

// Family feature pages (lazy loaded)
const FamilyCalendarPage = lazy(
  () => import('@/features/family-calendar/pages/FamilyCalendarPage')
);
const MemberProfilePage = lazy(
  () => import('@/features/family-calendar/pages/MemberProfilePage')
);
const AllEventsPage = lazy(
  () => import('@/features/family-calendar/pages/AllEventsPage')
);
const TodoListPage = lazy(
  () => import('@/features/todo-list/pages/TodoListPage')
);
const AlbumsPage = lazy(() => import('@/features/albums/pages/AlbumsPage'));
const NotificationsPage = lazy(
  () => import('@/features/notifications/pages/NotificationsPage')
);
const P2PChatPage = lazy(() => import('@/features/p2p-chat/pages/P2PChatPage'));
const SettingsPage = lazy(
  () => import('@/features/settings/pages/SettingsPage')
);
const OnboardingPage = lazy(
  () => import('@/features/family/pages/OnboardingPage')
);
const FamilySettingsPage = lazy(
  () => import('@/features/family/pages/FamilySettingsPage')
);
const SharedCalendarPage = lazy(
  () => import('@/features/family-calendar/pages/SharedCalendarPage')
);

/**
 * Invite deep links (`/onboarding?code=…&family=…`) must not dead-end on the
 * login page: unauthenticated visitors holding an invite code are sent
 * straight to the invited-signup variant of /register (code + family id are
 * forwarded). Everyone else gets the normal auth guard.
 */
function OnboardingGate() {
  const { isAuthenticated, isInitialized, loading } = useAuth();
  const [searchParams] = useSearchParams();

  // `isAuthenticated` is rehydrated from storage, so it only becomes
  // trustworthy once `checkAuth` has run (same gate as ProtectedRoute /
  // RoleRoute) — otherwise a stale value sends an invited visitor to signup.
  if (!isInitialized || loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated && searchParams.get('code')) {
    return <Navigate to={`/register?${searchParams.toString()}`} replace />;
  }

  return (
    <ProtectedRoute>
      <OnboardingPage />
    </ProtectedRoute>
  );
}

/**
 * A boundary that lives INSIDE the router.
 *
 * The app's original — and until now, only — `ErrorBoundary` is the one in
 * `main.tsx`, which wraps `<App />` and therefore sits ABOVE `BrowserRouter`.
 * That placement has two consequences, and both of them are bad:
 *
 *   1. A render-time throw anywhere, on any route, replaces the ENTIRE shell
 *      with the full-screen fallback. There is no page left, no header, no
 *      navigation — one broken widget takes down the app.
 *   2. The router is gone with it, so "Опитай отново" can only re-render the
 *      same tree at the same location. For a deterministic throw that means
 *      the fallback comes straight back, and the only real escape is
 *      "Начална страница", which is a hard `location.href` reload.
 *
 * `resetKey={location.pathname}` is what closes the second half: any
 * navigation — a nav link, the browser's back button, a programmatic redirect
 * — clears the caught error, and because the boundary is below the router,
 * those navigations are still possible while the fallback is showing. The
 * children keep their identity across the reset, so this costs nothing on the
 * ordinary path where no error was ever caught (see the `resetKey` docs on
 * `ErrorBoundary`; `key` would have remounted the whole route tree on every
 * navigation instead).
 *
 * "Опитай отново" stays a genuine retry: it remounts the failed subtree, which
 * is the right answer for the transient failures that dominate here — a lazy
 * chunk that lost the network mid-flight, a race in a just-mounted store. It
 * is deliberately NOT wired to navigate somewhere else; a button labelled
 * "try again" that silently teleports the user to the dashboard is a worse
 * lie than a button that tries again and fails.
 */
function RouteErrorBoundary({
  boundaryName,
  children,
}: {
  readonly boundaryName: string;
  readonly children: ReactNode;
}) {
  const location = useLocation();

  return (
    <ErrorBoundary boundaryName={boundaryName} resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Pathless layout route that puts a boundary between `MainLayout` and the
 * pages it renders into its `<Outlet />`.
 *
 * This is the placement that actually saves the shell: `MainLayout` is itself
 * a route element, so a boundary wrapping `<Routes>` still sits above the
 * header and sidebar and would take them down with the page. Nested here, a
 * throw inside any family-scoped page paints the fallback in the content area
 * and leaves the chrome — and therefore the way out — intact.
 */
function LayoutErrorBoundaryOutlet() {
  return (
    <RouteErrorBoundary boundaryName="layout">
      <Outlet />
    </RouteErrorBoundary>
  );
}

function RouteTable() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public auth routes - redirect to dashboard if already logged in */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* OAuth callback - public; completes the Supabase session */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Invite acceptance — the invite email token establishes the
            session; the page collects name/username/password. Not wrapped in
            PublicRoute (the user IS authenticated by the invite token). */}
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Public read-only shared calendar (token is the capability) */}
        <Route path="/shared/:token" element={<SharedCalendarPage />} />

        {/* Family onboarding — requires auth but NOT a family (standalone,
            outside MainLayout so no family-scoped chrome renders). Invite
            deep links from logged-out visitors go straight to signup. */}
        <Route path="/onboarding" element={<OnboardingGate />} />

        {/* Protected routes - require authentication */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Pathless layout route: everything MainLayout renders into its
              outlet is behind a route-scoped error boundary, so a page that
              throws does not take the header and navigation with it. */}
          <Route element={<LayoutErrorBoundaryOutlet />}>
            {/* Family-independent routes (profile-level) */}
            {/* Dev-only component showcase — not registered in production
              builds, so /demo falls through to the catch-all NotFound. */}
            {isDevelopment && <Route path="demo" element={<Demo />} />}
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="madivladi" element={<LovePage />} />

            {/* Family-scoped routes — require an active family (FamilyGate
              forces family-less users into /onboarding) */}
            <Route element={<FamilyGate />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route
                path="dashboard"
                element={
                  <Dashboard
                    MiniCalendarWidget={MiniCalendarWidgetContainer}
                    UpcomingEventsWidget={UpcomingEventsWidgetContainer}
                    TodoProgressWidget={TodoProgressWidgetContainer}
                    FamilyActivityWidget={FamilyActivityWidgetContainer}
                  />
                }
              />

              {/* Family management */}
              <Route path="family-settings" element={<FamilySettingsPage />} />

              {/* Family feature routes */}
              <Route path="family-calendar" element={<FamilyCalendarPage />} />
              <Route
                path="family-calendar/events"
                element={<AllEventsPage />}
              />
              <Route
                path="family-calendar/member/:memberId"
                element={<MemberProfilePage />}
              />
              <Route path="todo-list" element={<TodoListPage />} />
              <Route path="albums" element={<AlbumsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="chat" element={<P2PChatPage />} />

              {/* Dev-only monitoring routes — not registered in production
                builds, so /monitoring/* falls through to NotFound. */}
              {isDevelopment && (
                <Route path="monitoring">
                  <Route
                    index
                    element={<Navigate to="/monitoring/dashboard" replace />}
                  />
                  <Route path="dashboard" element={<MonitoringDashboard />} />
                  <Route path="config" element={<MonitoringConfig />} />
                  <Route path="logs" element={<MonitoringLogs />} />
                </Route>
              )}
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export function AppRouter() {
  return (
    <RouteErrorBoundary boundaryName="router">
      <RouteTable />
    </RouteErrorBoundary>
  );
}
